import { Group, MultiSelect, Stack, Switch, Text, TextInput } from '@mantine/core';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge, Button, Card, Pill } from '../ui';
import { useAssignLabs, useCohortAssignments, useUnassignLab } from '../../hooks/useGradebook';
import { useLabs } from '../../hooks/useLabs';

/**
 * Assign labs to a cohort with an optional due date. Assigned labs drive the
 * "Assigned only" gradebook view and the cohort report's completion tracking;
 * students see them under "assigned to you".
 */
export function CohortAssignments({ cohortId }: { cohortId: string }) {
  const labs = useLabs();
  const assigned = useCohortAssignments(cohortId);
  const assign = useAssignLabs(cohortId);
  const unassign = useUnassignLab(cohortId);
  const [picked, setPicked] = useState<string[]>([]);
  const [due, setDue] = useState('');
  const [required, setRequired] = useState(true);

  const assignedSlugs = useMemo(() => new Set((assigned.data ?? []).map((a) => a.lab_slug)), [assigned.data]);
  const options = useMemo(
    () =>
      (labs.data ?? [])
        .map((l) => ({ value: l.slug || l.id, label: l.title }))
        .filter((o) => o.value && !assignedSlugs.has(o.value)),
    [labs.data, assignedSlugs],
  );

  const submit = async () => {
    if (!picked.length) return;
    await assign.mutateAsync({
      lab_slugs: picked,
      due_at: due ? new Date(due + 'T23:59:00').toISOString() : null,
      required,
    });
    setPicked([]);
    setDue('');
  };

  return (
    <Stack gap="md">
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="sm">Assign labs</Text>
          <MultiSelect
            data={options}
            value={picked}
            onChange={setPicked}
            placeholder={labs.isPending ? 'Loading labs…' : 'Choose labs to assign'}
            searchable
            clearable
            aria-label="Labs to assign"
          />
          <Group align="flex-end" gap="md" wrap="wrap">
            <TextInput
              type="date"
              label="Due date (optional)"
              value={due}
              onChange={(e) => setDue(e.currentTarget.value)}
              leftSection={<CalendarClock size={14} />}
              style={{ width: 200 }}
            />
            <Switch label="Required" checked={required} onChange={(e) => setRequired(e.currentTarget.checked)} mb={6} />
            <Button variant="primary" size="sm" leftIcon={<Plus size={12} />} onClick={() => void submit()} loading={assign.isPending} disabled={!picked.length}>
              Assign
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600} size="sm">Assigned labs</Text>
            <Pill variant={(assigned.data?.length ?? 0) ? 'info' : 'warn'} size="xs">{assigned.data?.length ?? 0}</Pill>
          </Group>
          {(assigned.data?.length ?? 0) === 0 ? (
            <Text size="xs" c="dimmed">No labs assigned yet — the whole catalog is available to students, but nothing is tracked as required.</Text>
          ) : (
            (assigned.data ?? []).map((a) => (
              <Group key={a.lab_id} justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <Text size="sm">{a.lab_name}</Text>
                  {a.required ? <Badge variant="info" size="xs">required</Badge> : <Badge variant="neutral" size="xs">optional</Badge>}
                  {a.due_at ? (
                    <Text size="xs" c="dimmed">due {new Date(a.due_at).toLocaleDateString()}</Text>
                  ) : null}
                </Group>
                <Button variant="ghost" size="sm" leftIcon={<Trash2 size={12} />} onClick={() => unassign.mutate({ labId: a.lab_id })} loading={unassign.isPending && unassign.variables?.labId === a.lab_id}>
                  Remove
                </Button>
              </Group>
            ))
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
